import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Loader2, Plus, Trash2, Users, Award, FileText, Star, CheckSquare, AlertTriangle } from 'lucide-react';
import { evaluatorsAPI, usersAPI, applicationsAPI, programsAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const AdminEvaluatorAssignments = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [assignments, setAssignments] = useState([]);
  const [evaluators, setEvaluators] = useState([]);
  const [applications, setApplications] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // New state for multi-select
  const [selectedEvaluator, setSelectedEvaluator] = useState('');
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [programFilter, setProgramFilter] = useState('all');
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const t = {
    es: {
      title: 'Asignaciones de Evaluadores',
      subtitle: 'Asigna evaluadores a las postulaciones para su revisión',
      newAssignment: 'Nueva Asignación',
      evaluator: 'Evaluador',
      application: 'Postulación',
      program: 'Desafío',
      status: 'Estado',
      assignedAt: 'Fecha de Asignación',
      actions: 'Acciones',
      selectEvaluator: 'Seleccionar evaluador',
      selectApplications: 'Seleccionar postulaciones',
      filterByProgram: 'Filtrar por desafío',
      allPrograms: 'Todos los desafíos',
      assign: 'Asignar Seleccionadas',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      confirmDelete: '¿Estás seguro de eliminar esta asignación?',
      success: 'Asignaciones creadas correctamente',
      deleted: 'Asignación eliminada',
      error: 'Error al procesar',
      noAssignments: 'No hay asignaciones',
      noEvaluators: 'No hay evaluadores disponibles',
      noApplications: 'No hay postulaciones disponibles',
      statusLabels: {
        pending: 'Pendiente',
        in_progress: 'En progreso',
        completed: 'Completada'
      },
      score: 'Puntaje',
      stats: {
        totalAssignments: 'Total Asignaciones',
        pending: 'Pendientes',
        completed: 'Completadas'
      },
      selectedCount: 'seleccionadas',
      selectAll: 'Seleccionar todas',
      deselectAll: 'Deseleccionar todas',
      alreadyAssigned: 'Ya asignada',
      assignmentResults: 'Resultados de asignación',
      created: 'Creadas',
      skipped: 'Omitidas (ya existían)',
      applicant: 'Postulante',
      startup: 'Startup'
    },
    en: {
      title: 'Evaluator Assignments',
      subtitle: 'Assign evaluators to applications for review',
      newAssignment: 'New Assignment',
      evaluator: 'Evaluator',
      application: 'Application',
      program: 'Challenge',
      status: 'Status',
      assignedAt: 'Assigned Date',
      actions: 'Actions',
      selectEvaluator: 'Select evaluator',
      selectApplications: 'Select applications',
      filterByProgram: 'Filter by challenge',
      allPrograms: 'All challenges',
      assign: 'Assign Selected',
      cancel: 'Cancel',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete this assignment?',
      success: 'Assignments created successfully',
      deleted: 'Assignment deleted',
      error: 'Error processing',
      noAssignments: 'No assignments',
      noEvaluators: 'No evaluators available',
      noApplications: 'No applications available',
      statusLabels: {
        pending: 'Pending',
        in_progress: 'In Progress',
        completed: 'Completed'
      },
      score: 'Score',
      stats: {
        totalAssignments: 'Total Assignments',
        pending: 'Pending',
        completed: 'Completed'
      },
      selectedCount: 'selected',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      alreadyAssigned: 'Already assigned',
      assignmentResults: 'Assignment results',
      created: 'Created',
      skipped: 'Skipped (already existed)',
      applicant: 'Applicant',
      startup: 'Startup'
    }
  }[language];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assignmentsData, evaluatorsData, applicationsData, programsData] = await Promise.all([
        evaluatorsAPI.getAssignments(),
        usersAPI.getEvaluators(),
        applicationsAPI.getAll(),
        programsAPI.getAll()
      ]);
      setAssignments(assignmentsData);
      setEvaluators(evaluatorsData);
      setApplications(applicationsData);
      setPrograms(programsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: t.error, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setSelectedEvaluator('');
    setSelectedApplications([]);
    setProgramFilter('all');
    setDialogOpen(true);
  };

  // Check if an application is already assigned to the selected evaluator
  const isAlreadyAssigned = (applicationId) => {
    return assignments.some(
      a => a.evaluatorId === selectedEvaluator && a.applicationId === applicationId
    );
  };

  // Get filtered applications based on program filter
  const getFilteredApplications = () => {
    if (programFilter === 'all') {
      return applications;
    }
    return applications.filter(app => app.programId === programFilter);
  };

  // Toggle application selection
  const toggleApplicationSelection = (applicationId) => {
    if (isAlreadyAssigned(applicationId)) return;
    
    setSelectedApplications(prev => {
      if (prev.includes(applicationId)) {
        return prev.filter(id => id !== applicationId);
      } else {
        return [...prev, applicationId];
      }
    });
  };

  // Select/Deselect all filtered applications
  const toggleSelectAll = () => {
    const filtered = getFilteredApplications();
    const selectableApps = filtered.filter(app => !isAlreadyAssigned(app._id));
    
    if (selectedApplications.length === selectableApps.length) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(selectableApps.map(app => app._id));
    }
  };

  const handleCreateBulkAssignments = async () => {
    if (!selectedEvaluator || selectedApplications.length === 0) {
      toast({ 
        title: language === 'es' ? 'Selecciona un evaluador y al menos una postulación' : 'Select an evaluator and at least one application',
        variant: 'destructive' 
      });
      return;
    }

    try {
      setSaving(true);
      const result = await evaluatorsAPI.createBulkAssignments({
        evaluatorId: selectedEvaluator,
        applicationIds: selectedApplications,
        assignedBy: user._id || user.id
      });
      
      toast({ 
        title: t.success,
        description: `${t.created}: ${result.created}, ${t.skipped}: ${result.skipped}`
      });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating assignments:', error);
      toast({ 
        title: t.error, 
        description: error.response?.data?.detail || '',
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm(t.confirmDelete)) return;
    
    try {
      await evaluatorsAPI.deleteAssignment(assignmentId);
      toast({ title: t.deleted });
      fetchData();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast({ title: t.error, variant: 'destructive' });
    }
  };

  const handleOpenCleanup = async () => {
    setCleanupDialogOpen(true);
    setLoadingPreview(true);
    try {
      const preview = await evaluatorsAPI.getOrphanedSummary();
      setCleanupPreview(preview);
    } catch (error) {
      console.error('Error fetching orphaned summary:', error);
      toast({ title: t.error, variant: 'destructive' });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCleanup = async () => {
    setCleaningUp(true);
    try {
      const result = await evaluatorsAPI.cleanupOrphaned();
      toast({
        title: language === 'es' ? 'Limpieza completada' : 'Cleanup completed',
        description: language === 'es'
          ? `${result.deletedAssignments} asignaciones y ${result.deletedScores} puntajes eliminados`
          : `${result.deletedAssignments} assignments and ${result.deletedScores} scores deleted`
      });
      setCleanupDialogOpen(false);
      setCleanupPreview(null);
      fetchData();
    } catch (error) {
      console.error('Error cleaning up:', error);
      toast({ title: t.error, variant: 'destructive' });
    } finally {
      setCleaningUp(false);
    }
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getProgramName = (programId) => {
    const program = programs.find(p => p._id === programId);
    return program?.name || 'Unknown';
  };

  const pendingCount = assignments.filter(a => a.status === 'pending').length;
  const completedCount = assignments.filter(a => a.status === 'completed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
            <p className="text-gray-600 mt-1">{t.subtitle}</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={handleOpenCleanup}
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="cleanup-orphaned-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {language === 'es' ? 'Limpiar Datos Huérfanos' : 'Cleanup Orphaned Data'}
            </Button>
            <Button 
              onClick={handleOpenDialog} 
              className="bg-purple-600 hover:bg-purple-700"
              disabled={evaluators.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t.newAssignment}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-2 border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t.stats.totalAssignments}</p>
                  <p className="text-3xl font-bold text-gray-900">{assignments.length}</p>
                </div>
                <Users className="w-10 h-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-yellow-200 bg-yellow-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-800">{t.stats.pending}</p>
                  <p className="text-3xl font-bold text-yellow-900">{pendingCount}</p>
                </div>
                <FileText className="w-10 h-10 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-800">{t.stats.completed}</p>
                  <p className="text-3xl font-bold text-green-900">{completedCount}</p>
                </div>
                <Award className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Evaluators Overview */}
        {evaluators.length === 0 ? (
          <Card className="mb-8">
            <CardContent className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">{t.noEvaluators}</h3>
              <p className="text-gray-600 mt-2">
                {language === 'es' 
                  ? 'Crea usuarios con rol de evaluador primero' 
                  : 'Create users with evaluator role first'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                {language === 'es' ? 'Evaluadores Disponibles' : 'Available Evaluators'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {evaluators.map((evaluator) => (
                  <div 
                    key={evaluator._id} 
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{evaluator.name}</h4>
                        <p className="text-sm text-gray-500">{evaluator.email}</p>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3 text-sm">
                      <span className="text-yellow-600">
                        {evaluator.assignmentCount || 0} {language === 'es' ? 'asignaciones' : 'assignments'}
                      </span>
                      <span className="text-green-600">
                        {evaluator.scoreCount || 0} {language === 'es' ? 'evaluaciones' : 'scores'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Assignments Table */}
        <Card>
          <CardHeader>
            <CardTitle>{language === 'es' ? 'Todas las Asignaciones' : 'All Assignments'}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.evaluator}</TableHead>
                  <TableHead>{t.application}</TableHead>
                  <TableHead>{t.program}</TableHead>
                  <TableHead>{t.status}</TableHead>
                  <TableHead>{t.score}</TableHead>
                  <TableHead>{t.assignedAt}</TableHead>
                  <TableHead className="text-right">{t.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      {t.noAssignments}
                    </TableCell>
                  </TableRow>
                ) : (
                  assignments.map((assignment) => (
                    <TableRow key={assignment._id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{assignment.evaluatorName}</p>
                          <p className="text-sm text-gray-500">{assignment.evaluatorEmail}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{assignment.applicationName}</TableCell>
                      <TableCell>{assignment.programName}</TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeStyle(assignment.status)}>
                          {t.statusLabels[assignment.status] || assignment.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {assignment.status === 'completed' ? (
                          <span className="flex items-center gap-1 text-yellow-600 font-semibold">
                            <Star className="w-4 h-4" />
                            {assignment.weightedScore || '-'}
                          </span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {assignment.assignedAt 
                          ? new Date(assignment.assignedAt).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleDeleteAssignment(assignment._id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* New Assignment Dialog with Checkboxes */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5" />
                {t.newAssignment}
              </DialogTitle>
              <DialogDescription>
                {language === 'es' 
                  ? 'Selecciona un evaluador y las postulaciones que deseas asignarle.' 
                  : 'Select an evaluator and the applications you want to assign.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto space-y-6 py-4">
              {/* Evaluator Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">{t.evaluator} *</label>
                <Select 
                  value={selectedEvaluator} 
                  onValueChange={(value) => {
                    setSelectedEvaluator(value);
                    setSelectedApplications([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectEvaluator} />
                  </SelectTrigger>
                  <SelectContent>
                    {evaluators.map((evaluator) => (
                      <SelectItem key={evaluator._id} value={evaluator._id}>
                        {evaluator.name} ({evaluator.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Program Filter */}
              <div className="space-y-2">
                <label className="text-sm font-medium">{t.filterByProgram}</label>
                <Select 
                  value={programFilter} 
                  onValueChange={setProgramFilter}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.allPrograms}</SelectItem>
                    {programs.map((program) => (
                      <SelectItem key={program._id} value={program._id}>
                        {program.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Applications List with Checkboxes */}
              {selectedEvaluator && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">
                      {t.selectApplications} ({selectedApplications.length} {t.selectedCount})
                    </label>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={toggleSelectAll}
                    >
                      {selectedApplications.length === getFilteredApplications().filter(a => !isAlreadyAssigned(a._id)).length
                        ? t.deselectAll
                        : t.selectAll}
                    </Button>
                  </div>
                  
                  <div className="border-2 border-gray-200 rounded-lg max-h-80 overflow-y-auto">
                    {getFilteredApplications().length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        {t.noApplications}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {getFilteredApplications().map((app) => {
                          const alreadyAssigned = isAlreadyAssigned(app._id);
                          const isSelected = selectedApplications.includes(app._id);
                          
                          return (
                            <div
                              key={app._id}
                              className={`p-4 flex items-start gap-4 transition-colors ${
                                alreadyAssigned 
                                  ? 'bg-gray-50 opacity-60' 
                                  : isSelected 
                                    ? 'bg-purple-50' 
                                    : 'hover:bg-gray-50 cursor-pointer'
                              }`}
                              onClick={() => !alreadyAssigned && toggleApplicationSelection(app._id)}
                            >
                              <div className="pt-0.5 shrink-0">
                                <Checkbox
                                  checked={isSelected}
                                  disabled={alreadyAssigned}
                                  className="data-[state=checked]:bg-purple-600 pointer-events-none"
                                />
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900 truncate">
                                    {app.startupName}
                                  </h4>
                                  {alreadyAssigned && (
                                    <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                                      {t.alreadyAssigned}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                  <span>{t.applicant}: {app.founderName}</span>
                                  <span>•</span>
                                  <span>{t.program}: {getProgramName(app.programId)}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter className="border-t pt-4">
              <div className="flex items-center justify-between w-full">
                <span className="text-sm text-gray-500">
                  {selectedApplications.length > 0 && (
                    <span className="text-purple-600 font-medium">
                      {selectedApplications.length} {t.selectedCount}
                    </span>
                  )}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    {t.cancel}
                  </Button>
                  <Button 
                    onClick={handleCreateBulkAssignments} 
                    disabled={saving || !selectedEvaluator || selectedApplications.length === 0}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t.assign} ({selectedApplications.length})
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Cleanup Orphaned Data Dialog */}
        <Dialog open={cleanupDialogOpen} onOpenChange={setCleanupDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                {language === 'es' ? 'Limpiar Datos Huérfanos' : 'Cleanup Orphaned Data'}
              </DialogTitle>
              <DialogDescription>
                {language === 'es'
                  ? 'Se eliminarán asignaciones y puntajes que apuntan a desafíos o postulaciones que ya no existen.'
                  : 'This will remove assignments and scores that reference deleted challenges or applications.'}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {loadingPreview ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : cleanupPreview ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200 text-center">
                      <p className="text-sm text-red-700 mb-1">
                        {language === 'es' ? 'Asignaciones huérfanas' : 'Orphaned assignments'}
                      </p>
                      <p className="text-3xl font-bold text-red-800">{cleanupPreview.orphanedAssignments}</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200 text-center">
                      <p className="text-sm text-red-700 mb-1">
                        {language === 'es' ? 'Puntajes huérfanos' : 'Orphaned scores'}
                      </p>
                      <p className="text-3xl font-bold text-red-800">{cleanupPreview.orphanedScores}</p>
                    </div>
                  </div>

                  {(cleanupPreview.orphanedAssignments === 0 && cleanupPreview.orphanedScores === 0) ? (
                    <div className="text-center py-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-green-700 font-medium">
                        {language === 'es' ? 'No hay datos huérfanos para limpiar' : 'No orphaned data to clean up'}
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {cleanupPreview.assignments?.map((a, i) => (
                        <div key={i} className="text-xs p-2 bg-gray-50 rounded border border-gray-200 flex justify-between">
                          <span className="text-gray-600">
                            {language === 'es' ? 'Asignación' : 'Assignment'}: {a.assignmentId?.slice(-6)}
                          </span>
                          <div className="flex gap-2">
                            {!a.programExists && (
                              <Badge variant="outline" className="text-red-600 border-red-300 text-[10px]">
                                {language === 'es' ? 'Desafío eliminado' : 'Challenge deleted'}
                              </Badge>
                            )}
                            {!a.applicationExists && (
                              <Badge variant="outline" className="text-red-600 border-red-300 text-[10px]">
                                {language === 'es' ? 'Postulación eliminada' : 'Application deleted'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                      {cleanupPreview.scores?.map((s, i) => (
                        <div key={`s-${i}`} className="text-xs p-2 bg-gray-50 rounded border border-gray-200 flex justify-between">
                          <span className="text-gray-600">
                            {language === 'es' ? 'Puntaje' : 'Score'}: {s.scoreId?.slice(-6)}
                          </span>
                          <div className="flex gap-2">
                            {!s.programExists && (
                              <Badge variant="outline" className="text-red-600 border-red-300 text-[10px]">
                                {language === 'es' ? 'Desafío eliminado' : 'Challenge deleted'}
                              </Badge>
                            )}
                            {!s.applicationExists && (
                              <Badge variant="outline" className="text-red-600 border-red-300 text-[10px]">
                                {language === 'es' ? 'Postulación eliminada' : 'Application deleted'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCleanupDialogOpen(false)}>
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                data-testid="confirm-cleanup-btn"
                onClick={handleCleanup}
                disabled={cleaningUp || !cleanupPreview || (cleanupPreview.orphanedAssignments === 0 && cleanupPreview.orphanedScores === 0)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {cleaningUp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {language === 'es' ? 'Confirmar Limpieza' : 'Confirm Cleanup'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminEvaluatorAssignments;
